import React from 'react'
import { Package, CheckCircle, Percent } from 'lucide-react'
import type { BatchMetrics } from '../../lib/api'

interface MetricCardProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  value: string | number
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon: Icon,
  label,
  value,
}) => {
  return (
    <div className='bg-white rounded-lg shadow p-6 border border-gray-200 flex items-center justify-between'>
      <Icon className='w-10 h-10 text-blue-700 flex-shrink-0' />
      <div className='text-right'>
        <div className='inline-block text-center'>
          <div className='text-3xl font-bold text-gray-900'>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          <div className='text-sm text-gray-600 mt-0.5'>{label}</div>
        </div>
      </div>
    </div>
  )
}

interface GlobalMetricsCardsProps {
  metrics: BatchMetrics | null
  loading?: boolean
}

export const GlobalMetricsCards: React.FC<GlobalMetricsCardsProps> = ({
  metrics,
  loading = false,
}) => {
  const skeleton = (
    <div className='bg-white rounded-lg shadow p-6 border border-gray-200 animate-pulse'>
      <div className='flex items-center justify-between mb-2'>
        <div className='w-8 h-8 rounded-full bg-slate-200' />
      </div>
      <div className='w-20 h-7 rounded-full bg-slate-200 mb-2' />
      <div className='w-24 h-3 rounded-full bg-slate-200' />
    </div>
  )

  if (loading || !metrics) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
        {[0, 1, 2, 3].map(idx => (
          <React.Fragment key={`skeleton-${idx}`}>{skeleton}</React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
      <MetricCard
        icon={Package}
        label='Total Batches'
        value={metrics.totalBatches || 0}
      />
      <MetricCard
        icon={Package}
        label='Total Products'
        value={metrics.totalProducts || 0}
      />
      <MetricCard
        icon={CheckCircle}
        label='Total Matched'
        value={metrics.totalMatched || 0}
      />
      <MetricCard
        icon={Percent}
        label='Overall Coverage'
        value={(() => {
          const pct = metrics.overallCoverage || 0
          if (pct > 0 && pct < 0.1) return pct.toFixed(2) + '%'
          if (pct > 0 && pct < 10) return pct.toFixed(1) + '%'
          return Math.round(pct) + '%'
        })()}
      />
    </div>
  )
}

export default GlobalMetricsCards
